import axios from 'axios';
import { Dispatch } from 'redux';

// Action Types
const GET_COUNTRIES = 'GET_COUNTRIES';
const GET_COUNTRY_DATA = 'GET_COUNTRY_DATA';
const GET_COUNTRY_CMS_DATA = 'GET_COUNTRY_CMS_DATA';

// Action Interfaces
interface GetCountriesAction {
  type: typeof GET_COUNTRIES;
  payload: Country[];
}
interface GetCountryDataAction { // New interface for the country data action
  type: typeof GET_COUNTRY_DATA;
  payload: CountryData;
}
interface GetCountryCmsDataAction {
  type: typeof GET_COUNTRY_CMS_DATA;
  payload: CountryCmsData[];
}

// Define the Country type
interface Country {
  id: number;
  name: string;
  slug: string;
  nicename: string;
  code: string;
  continentId: string;
  activated:boolean;
  content_cms_entry__: string;
  numCities: number;
  numTours?: number;
  mainImage?: string | null;
  nameTranslated?: string;
}

interface CountryData {
  id: number;
  name: string;
  slug: string;
  nicename: string;
  code: string;
  continentId: string;
  activated:boolean;
  content_cms_entry__: string;
  numCities: number;
}

interface CountryCmsData {
  title: string;
  short_content: string;
  content: string;
  mainImage?: string;
}

// Async Action Creator
export const getCountries = (continentId: string | null, activated?: boolean | null, language?: string) => async (dispatch: Dispatch<GetCountriesAction>) => {
  try {
    const params: Record<string, any> = { continentId, language };

    if (activated !== null && activated !== undefined) {
      params.activated = activated;
    }

    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/countries`, {
      params,
    });

    // Only ever store an ARRAY into the shared `countries` slice — never a
    // non-array body (e.g. the 404 "No countries found" message), and never
    // clear it on error. Many components (header mega-menu, signup, account…)
    // fill this same slice with all-countries via getCountries(null); wiping it
    // would break them. ListCountries filters by continentId client-side, so a
    // continent with no countries just yields an empty filtered list + its
    // tokenised empty-state without touching the slice.
    if (Array.isArray(response.data)) {
      dispatch({ type: GET_COUNTRIES, payload: response.data });
    }
  } catch (error) {
    console.error('Error fetching countries:', error);
  }
};

export const getCountryData = (slug: string) => async (dispatch: Dispatch<GetCountryDataAction>) => {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/country/data`, {
      params: { slug: slug }
    });

    if (response.data) {
      dispatch({
        type: GET_COUNTRY_DATA,
        payload: response.data,
      });
    } else {
      // Handle the case when data is not available
      // You can dispatch another action or handle it as needed
    }
  } catch (error) {
    console.error('Error fetching country data:', error);
    // You can dispatch an error action or handle it as needed
  }
};

export const getCountryCmsData = (content_cms_entry__: string, language: string) => async (dispatch: Dispatch<GetCountryCmsDataAction>) => {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/country/cms/data`, {
      params: { content_cms_entry__, language }
    });

    // If the response is successful, dispatch the data to the store
    dispatch({
      type: GET_COUNTRY_CMS_DATA,
      payload: response.data,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404 && language !== 'en_US') {
      // If there's a 404 error and the language is not already 'en_US', try again with 'en_US'
      try {
        const fallbackResponse = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/country/cms/data`, {
          params: { content_cms_entry__, language: 'en_US' }
        });

        dispatch({
          type: GET_COUNTRY_CMS_DATA,
          payload: fallbackResponse.data,
        });
      } catch (fallbackError) {
        console.error('Error fetching country CMS data with fallback language:', fallbackError);
        // Handle further error (maybe dispatch an error action)
      }
    } else {
      // Handle other errors or a 404 when the language is already 'en_US'
      console.error('Error fetching country CMS data:', error);
      // Handle error (maybe dispatch an error action)
    }
  }
};


// State Type
interface CountriesState {
  countries: Country[];
  currentCountryData: CountryData | null;
  countryCmsData: CountryCmsData[] | null;
}

// Initial State
const initialState: CountriesState = {
  countries: [],
  currentCountryData: null,
  countryCmsData: null,
};

// Reducer
const countriesReducer = (
    state: CountriesState = initialState, 
    action: GetCountriesAction | GetCountryDataAction | GetCountryCmsDataAction // Include the new action type
  ) => {
  switch (action.type) {
    case GET_COUNTRIES: {
      return {
        ...state,
        countries: action.payload,
      };
    }
    case GET_COUNTRY_DATA: {
      const countryDataAction = action as GetCountryDataAction;
      return {
        ...state,
        currentCountryData: countryDataAction.payload,
      };
    }
    case GET_COUNTRY_CMS_DATA: {
      return {
        ...state,
        countryCmsData: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

export default countriesReducer;
